<template>
  <div class="page page-streak">
    <!-- Top Bar matching Profile Page -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="logoUrl" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchStreakInfo">重试</button>
    </div>

    <!-- Main Content -->
    <div v-else class="phone">
      <div class="phone-inner">
        <div class="streak-main">
          <div class="flame-wrap">
            <img :src="flameUrl" class="flame-img" alt="flame" />
          </div>
          <div class="streak-number">
            {{ streakInfo.streakSpark || 0 }}
          </div>
          <div class="streak-label">连续火花</div>
        </div>

        <div class="intro-card">
          <div class="intro-avatar-wrapper">
             <div class="intro-avatar">
              <img
                v-if="streakInfo.avatarUrl"
                :src="streakInfo.avatarUrl"
                alt="avatar"
              />
              <div v-else class="intro-avatar-placeholder">头像</div>
            </div>
          </div>

          <div class="intro-text">
            <div class="intro-name">
              我是{{ streakInfo.userName || '用户' }}
            </div>
            <p class="intro-desc">
              我在Kukukao已经坚持打卡<span class="highlight">{{ streakInfo.streakSpark || 0 }}</span>天啦，快来<br>
              跟我一起开启法考闯关之旅吧
            </p>
          </div>
        </div>

        <open-app-button text="打开app查看" @open-app="handleOpenApp" />
      </div>
    </div>
  </div>
</template>

<script>
import OpenAppButton from '../components/OpenAppButton.vue'
import logo from '@/assets/logo.svg'
import flame from '@/assets/streak/streak-flame.svg'
import { openApp } from '@/utils/openApp'

export default {
  name: 'HomeStreak',
  components: {
    OpenAppButton
  },
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      logoUrl: logo,
      flameUrl: flame,
      streakInfo: {},
      loading: true,
      error: null
    }
  },
  computed: {
    userId() {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    }
  },
  mounted() {
    this.fetchStreakInfo()
  },
  methods: {
    async fetchStreakInfo() {
      if (!this.userId) {
        this.error = '缺少用户ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/share/getStreakInfo?userId=${this.userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

        if (result.code === 200 && result.data) {
          this.streakInfo = result.data
        } else {
          this.error = result.message || '获取数据失败'
        }
      } catch (err) {
        console.error('获取streak信息失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-streak {
  min-height: 100vh;
  padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* 为固定头部留出空间 */
  // Use a vibrant orange background similar to the design
  background: #FEB938;
}

// Custom Top Bar：固定顶部，不随页面滚动
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 20px 12px 20px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
     width: 32px; height: 32px;
     margin-right: 8px;
  }
  .brand-text {
     font-size: 18px;
     font-weight: 700;
     color: #f15b5b; // Brand Color
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
}

.phone {
  padding: 0 12px 12px;
}

.phone-inner {
  border-radius: 26px;
  background: transparent;
  padding: 10px 16px 0;
}

.streak-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 40px;
  padding-bottom: 30px;
}

.flame-wrap {
  width: 180px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  .flame-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    // Add a slight drop shadow/glow effect if needed, but SVG usually carries it
    filter: drop-shadow(0 4px 12px rgba(228, 62, 20, 0.3));
  }
}

.streak-number {
  font-size: 72px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
  text-shadow: 0 4px 12px rgba(215, 127, 0, 0.4);
}

.streak-label {
  margin-top: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  opacity: 0.95;
}

.intro-card {
  margin-top: 40px;
  background: #ffffff;
  border-radius: 24px;
  padding: 0 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;
}

.intro-avatar-wrapper {
  margin-top: -33px; // Pull avatar up to overlap the top border
  padding: 4px;
  background: #fff; // Optional: white border effect around avatar
  border-radius: 50%;
}

.intro-avatar {
  width: 66px;
  height: 66px;
  border-radius: 50%;
  overflow: hidden;
  background: #ffe3dd;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #66CCCC; // Cyan border from design

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.intro-avatar-placeholder {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.intro-text {
  margin-top: 12px;
  width: 100%;
}

.intro-name {
  font-size: 18px;
  font-weight: 800;
  color: #333;
  margin-bottom: 8px;
}

.intro-desc {
  font-size: 14px;
  color: #888;
  line-height: 1.6;
}

.highlight {
  color: #FF9900;
  font-weight: 700;
  font-size: 15px;
  margin: 0 2px;
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #fff;
}

// Error State
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.error-text {
  font-size: 14px;
  color: #fff;
  margin-bottom: 16px;
}

.btn-retry {
  background: #fff;
  color: #FEB938;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}
</style>

